import { FC, useState } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import dayjs from 'dayjs';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import type { Citation } from '@frontend/task-center-scope';
import { Text, Avatar, SpinningLoader, Button, useAlert, NakedButton } from '@frontend/design-system';
import { useGetCitationPersonName } from '../../../hooks';
import { taskCenterTrackingIds } from '../../../tracking-ids';
import {
  citationContainerStyle,
  messageStyle,
  alignRight,
  noCitationContainerStyle,
  reverseFlex,
  alignLeft,
  textStrikeThrough,
  deletedTaskCitationStyle,
  outboundMessageBackground,
  deletedMessageStyle,
} from './styles';

interface MessageCitationProps {
  locationId: string;
  sourceId?: string;
  citation?: Citation;
  isTaskDeleted: boolean;
}

export const MessageCitation: FC<MessageCitationProps> = ({ locationId, sourceId, citation, isTaskDeleted }) => {
  const { t } = useTranslation('task-center');
  const { error: errorAlert } = useAlert();
  const fetchGetSMS = SMSDataV3.Queries.useFetchGetSMS();
  const [isLoading, setIsLoading] = useState(false);
  const messageOutbound = citation?.direction && citation.direction === Direction.DIRECTION_OUTBOUND;

  const { onClick: openMiniChatThread } = useMessageAction();
  const { closeThread, threadParams } = useInboxNavigate();
  const date = citation?.created_at ? dayjs(citation?.created_at).format('MMM DD, YYYY, hh mm A') : '';
  const { personName, isLoading: isFetchingPersonName } = useGetCitationPersonName({ citation });

  const onClick = async () => {
    setIsLoading(true);
    if (!sourceId) {
      setIsLoading(false);
      return;
    }

    try {
      const result = await fetchGetSMS({
        request: {
          smsId: sourceId,
          locationId,
        },
      });
      if (!result.sms.id) {
        // This should never happen, because the BE should return an error instead, but to be safe, error handling can
        // be added here as well.
        setIsLoading(false);
        return;
      }
      if (threadParams?.filter && threadParams?.threadId === result.sms.threadId) {
        closeThread();
      }
      openMiniChatThread({
        threadGroupId: locationId,
        threadId: result.sms.threadId,
        targetSmsData: {
          id: result.sms.id,
          createdAt: result.sms.createdAt,
        },
      });
    } catch (error) {
      // TODO: sending it to sentry would be a good idea?
      errorAlert(t('Unable to open message at this moment'));
    }
    setIsLoading(false);
  };

  if (!citation) {
    return (
      <div css={noCitationContainerStyle}>
        <Icon name='message' size={16} />{' '}
        <Text size='large' weight='bold'>
          {t('View Details in Inbox')}
        </Text>
        <Button
          variant='tertiary'
          loading={isLoading}
          css={alignRight}
          onClick={onClick}
          trackingId={taskCenterTrackingIds.openMessage}
        >
          {t('View')}
        </Button>
      </div>
    );
  }

  return (
    <NakedButton
      css={[citationContainerStyle, isTaskDeleted && deletedTaskCitationStyle]}
      onClick={onClick}
      trackingId={taskCenterTrackingIds.openMessageWithCitation}
    >
      <span>
        <Icon name='inbox' size={16} />
        <Text size='large' weight='bold'>
          {t('Inbox')}
        </Text>
      </span>
      <span css={messageOutbound && reverseFlex} className='message-citation'>
        {isFetchingPersonName ? <SpinningLoader /> : <Avatar name={personName} isUser={!!citation.user_id} />}
        {citation?.description && (
          <span css={[messageStyle, messageOutbound && outboundMessageBackground]}>
            <Text size='large' css={[isTaskDeleted && textStrikeThrough]}>
              {citation?.description}
            </Text>
            {isTaskDeleted && (
              <span css={deletedMessageStyle}>
                <Icon name='warning-badge' color='warn' size={16} />
                <Text size='small'>{t('This message was deleted.')}</Text>
              </span>
            )}
          </span>
        )}
      </span>
      <span css={messageOutbound && reverseFlex}>
        {personName && (
          <Text size='medium' weight='bold'>
            {personName}
          </Text>
        )}
        <Text color='light' size='small' css={messageOutbound ? alignLeft : alignRight}>
          {date}
        </Text>
      </span>
    </NakedButton>
  );
};
